import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import enGBLocale from '@fullcalendar/core/locales/en-gb';
import { 
  Box, 
  Button, 
  Typography, 
  useTheme, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Paper,
  Chip,
  Avatar,
  IconButton,
  Card,
  CardContent,
  Fade,
  Backdrop,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import { 
  CalendarToday,
  Person,
  Assessment,
  VideoCall,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface Event {
  id: string;
  title: string;
  start: string;
  end: string;
  type: 'assessment' | 'session' | 'interview' | 'group_supervision';
  assessor?: string;
  counsellor?: string;
  counsellorName?: string;
  interviewer?: string;
  interviewerName?: string;
  client?: string;
  status?: string;
  notes?: string;
  participants?: string[]; // For group supervision
}

const Calendar: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [eventListOpen, setEventListOpen] = useState(false);
  const [eventListType, setEventListType] = useState<'all' | 'assessment' | 'session' | 'interview' | 'group_supervision'>('all');
  const [eventListTab, setEventListTab] = useState(0); // 0 = Future, 1 = Past
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';
  const theme = useTheme();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await api.get('/api/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const handleEventClick = (info: any) => {
    const event = info.event;
    setSelectedEvent(event);
  };

  const handleCloseDialog = () => {
    setSelectedEvent(null);
  };

  const handleViewDetails = () => {
    const eventData = selectedEvent._def.extendedProps;
    if (eventData.type === 'assessment' && eventData.client) {
      navigate(`/assessment-part-1/${eventData.client}`);
    } else if (eventData.type === 'session' && eventData.client) {
      navigate(`/client-details/${eventData.client}`);
    } else if (eventData.type === 'interview' && eventData.counsellor) {
      navigate(`/counsellors/${eventData.counsellor}`);
    }
    handleCloseDialog();
  };

  const handleDateSelect = (selectInfo: any) => {
    // Handle date selection - show create event modal
    console.log('Date selected:', selectInfo);
  };

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'assessment':
        return {
          backgroundColor: '#6366f1',
          borderColor: '#4f46e5',
          textColor: '#ffffff'
        };
      case 'session':
        return {
          backgroundColor: '#48bb78',
          borderColor: '#38a169',
          textColor: '#ffffff'
        };
      case 'interview':
        return {
          backgroundColor: '#f59e0b',
          borderColor: '#d97706',
          textColor: '#ffffff'
        };
      case 'group_supervision':
        return {
          backgroundColor: '#9333ea',
          borderColor: '#7e22ce',
          textColor: '#ffffff'
        };
      default:
        return {
          backgroundColor: theme.palette.grey[500],
          borderColor: theme.palette.grey[600],
          textColor: '#ffffff'
        };
    }
  };

  const getRoleBasedTitle = () => {
    switch (user?.role) {
      case 'administrator':
        return 'Calendar - All Events';
      case 'assessor':
        return 'Assessment Calendar';
      case 'counsellor':
        return 'My Appointments';
      default:
        return 'Calendar';
    }
  };

  const getRoleBasedDescription = () => {
    switch (user?.role) {
      case 'administrator':
        return 'View and manage all assessments and counselling sessions';
      case 'assessor':
        return 'View your scheduled assessments';
      case 'counsellor':
        return 'View your client appointments and sessions';
      default:
        return 'Calendar view';
    }
  };

  // Filter events based on user role
  const getFilteredEvents = () => {
    return events.filter(event => {
      switch (user?.role) {
        case 'administrator':
          return true; // Admin sees all events
        case 'assessor':
          return event.type === 'assessment'; // Assessor sees only assessments
        case 'counsellor':
          // Counsellors see only sessions and group supervision (not assessments or interviews)
          if (event.type === 'assessment' || event.type === 'interview') {
            return false;
          }
          // Show sessions assigned to this counsellor
          if (event.type === 'session' && event.counsellor === user._id) {
            return true;
          }
          // Show group supervision where counsellor is a participant
          if (event.type === 'group_supervision' && event.participants?.includes(user._id)) {
            return true;
          }
          return false;
        default:
          return false;
      }
    });
  };

  // Get future events only
  const getFutureEvents = () => {
    const now = new Date();
    return getFilteredEvents().filter(event => new Date(event.start) > now);
  };

  // Get past events only
  const getPastEvents = () => {
    const now = new Date();
    return getFilteredEvents().filter(event => new Date(event.start) <= now);
  };

  // Get events for list display based on type and tab
  const getEventsForList = () => {
    const futureEvents = getFutureEvents();
    const pastEvents = getPastEvents();
    const eventsToShow = eventListTab === 0 ? futureEvents : pastEvents;
    
    if (eventListType === 'all') {
      return eventsToShow;
    }
    return eventsToShow.filter(event => event.type === eventListType);
  };

  const handleEventListOpen = (type: 'all' | 'assessment' | 'session' | 'interview' | 'group_supervision') => {
    setEventListType(type);
    setEventListOpen(true);
  };

  const handleEventListClose = () => {
    setEventListOpen(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setEventListTab(newValue);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 25%, #312e81 75%, #1e3a8a 100%)',
      p: { xs: 1, sm: 2, md: 3 }
    }}>
      <Paper 
        elevation={3}
        sx={{ 
          mb: { xs: 2, sm: 3 }, 
          p: { xs: 2, sm: 3 }, 
          backgroundColor: 'rgba(255, 255, 255, 0.05)', 
          backdropFilter: 'blur(10px)' 
        }}
      >
        {/* Modern Header */}
        <Box sx={{ 
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: 'white',
          p: { xs: 2, sm: 3, md: 4 }
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2, sm: 0 }, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, order: { xs: 1, sm: isAdmin ? 2 : 1 } }}>
              <Avatar sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)', 
                width: { xs: 40, sm: 48, md: 56 }, 
                height: { xs: 40, sm: 48, md: 56 },
                border: '2px solid rgba(255, 255, 255, 0.3)'
              }}>
                <CalendarToday sx={{ fontSize: { xs: 20, sm: 24, md: 28 } }} />
              </Avatar>
              <Box sx={{ textAlign: { xs: 'left', sm: isAdmin ? 'right' : 'left' } }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                  mb: 0.5,
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  {getRoleBasedTitle()}
                </Typography>
                <Typography variant="subtitle1" sx={{ 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}>
                  {getRoleBasedDescription()}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' }, order: { xs: 2, sm: isAdmin ? 1 : 2 } }}>
              <Button
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  px: { xs: 2, sm: 3 },
                  py: { xs: 1, sm: 1.5 },
                  backgroundColor: '#2196f3',
                  '&:hover': { backgroundColor: '#1976d2' }
                }}
              >
                Back to Home
              </Button>
            </Box>
          </Box>
          
          {/* Role-based Stats - Future Events Only */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 3 } }}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                minWidth: { xs: 'auto', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => handleEventListOpen('all')}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  {getFutureEvents().length}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  {user?.role === 'counsellor' ? 'All Appointments' : 'Future Events'}
                </Typography>
              </CardContent>
            </Card>
            
            {/* Show different cards based on user role */}
            {user?.role === 'counsellor' ? (
              <>
                {/* Counsellors see Sessions and Group Supervision */}
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('session')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'session').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Client Sessions
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('group_supervision')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'group_supervision').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Group Supervision
                    </Typography>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Admins and Assessors see all event types */}
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('assessment')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'assessment').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Assessments
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('session')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'session').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Sessions
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('interview')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'interview').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Interviews
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card 
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    minWidth: 120,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.25)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                  onClick={() => handleEventListOpen('group_supervision')}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {getFutureEvents().filter(e => e.type === 'group_supervision').length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Group Supervision
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        </Box>

        {/* Calendar Section */}
        <Box sx={{ p: { xs: 1, sm: 2, md: 4 } }}>
          <Box sx={{ 
            height: { xs: 'calc(100vh - 500px)', sm: 'calc(100vh - 450px)', md: 'calc(100vh - 400px)' },
            minHeight: { xs: '400px', sm: '500px', md: '600px' },
            '& .fc': {
              fontFamily: 'inherit'
            },
            '& .fc-header-toolbar': {
              mb: 3,
              '& .fc-toolbar-chunk': {
                display: 'flex',
                alignItems: 'center'
              }
            },
            '& .fc-button-primary': {
              backgroundColor: '#4f46e5',
              borderColor: '#4f46e5',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#4338ca',
                borderColor: '#4338ca'
              },
              '&:focus': {
                boxShadow: '0 0 0 2px rgba(79, 70, 229, 0.25)'
              }
            },
            '& .fc-button-primary:disabled': {
              backgroundColor: '#e2e8f0',
              borderColor: '#e2e8f0',
              color: '#a0aec0'
            },
            '& .fc-today-button': {
              backgroundColor: '#48bb78',
              borderColor: '#48bb78',
              '&:hover': {
                backgroundColor: '#38a169',
                borderColor: '#38a169'
              }
            },
            '& .fc-daygrid-day': {
              '&:hover': {
                backgroundColor: 'rgba(79, 70, 229, 0.05)'
              }
            },
            '& .fc-timegrid-slot': {
              borderColor: 'rgba(0, 0, 0, 0.08)'
            },
            '& .fc-col-header': {
              backgroundColor: 'rgba(79, 70, 229, 0.08)',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.95)'
            },
            '& .fc-daygrid-day-number': {
              color: 'rgba(255, 255, 255, 0.9)'
            },
            '& .fc-timegrid-axis': {
              color: 'rgba(255, 255, 255, 0.8)'
            },
            '& .fc-timegrid-slot-label': {
              color: 'rgba(255, 255, 255, 0.8)'
            },
            '& .fc-toolbar-title': {
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 700
            }
          }}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              locale={enGBLocale}
              headerToolbar={{
                left: 'prev,next',
                center: 'title',
                right: 'today'
              }}
              initialView="dayGridMonth"
              editable={user?.role === 'administrator'}
              selectable={user?.role === 'administrator'}
              selectMirror={true}
              dayMaxEvents={2}
              moreLinkContent={(args) => `+${args.num}`}
              weekends={true}
              events={getFilteredEvents()}
              eventClick={handleEventClick}
              eventContent={(eventInfo) => {
                const eventData = eventInfo.event._def;
                const type = eventData.extendedProps.type;
                const clientName = eventData.title;
                const counsellorName = eventData.extendedProps.counsellorName;
                const colors = getEventColor(type);
                
                return (
                  <Box sx={{ 
                    p: 1,
                    backgroundColor: colors.backgroundColor,
                    color: colors.textColor,
                    borderRadius: 2,
                    width: '100%',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    border: `2px solid ${colors.borderColor}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {type === 'assessment' ? 
                        <Assessment sx={{ fontSize: 14 }} /> : 
                        type === 'interview' ?
                        <Person sx={{ fontSize: 14 }} /> :
                        <VideoCall sx={{ fontSize: 14 }} />
                      }
                      <Typography noWrap variant="subtitle2" sx={{ 
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}>
                        {clientName}
                      </Typography>
                    </Box>
                    <Typography noWrap variant="caption" sx={{ 
                      opacity: 0.9,
                      fontSize: '0.65rem'
                    }}>
                      {type === 'session' ? `Session - ${counsellorName}` : 
                       type === 'interview' ? `Interview - ${eventData.extendedProps.interviewerName || 'Interviewer'}` :
                       'Assessment'}
                    </Typography>
                  </Box>
                );
              }}
              select={handleDateSelect}
              height="100%"
            />
          </Box>
        </Box>
      </Paper>
      {/* Enhanced Event Details Dialog */}
      <Dialog 
        open={!!selectedEvent} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ 
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  {selectedEvent._def.extendedProps.type === 'assessment' ? 
                    <Assessment /> : <VideoCall />
                  }
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedEvent._def.title}
                  </Typography>
                  <Chip 
                    label={selectedEvent._def.extendedProps.type === 'session' ? 'Session' : 'Assessment'}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Box>
              <IconButton 
                onClick={handleCloseDialog}
                sx={{ color: 'white' }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Card sx={{ 
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'rgba(255, 255, 255, 0.95)' }}>
                      Event Details
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Person sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            {selectedEvent._def.extendedProps.type === 'session' ? 
                              `Session with ${selectedEvent._def.extendedProps.counsellorName}` : 
                              selectedEvent._def.extendedProps.type === 'interview' ?
                              `Interview with ${selectedEvent._def.extendedProps.interviewerName}` :
                              'Assessment'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CalendarToday sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            Date & Time
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            {new Date(selectedEvent.start).toLocaleDateString('en-GB', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                            {new Date(selectedEvent.start).toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit'
                            })} - {new Date(selectedEvent.end).toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit'
                            })}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {selectedEvent._def.extendedProps.type === 'interview' && selectedEvent._def.extendedProps.meetingLink && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <VideoCall sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                              Google Meet
                            </Typography>
                            <a
                              href={selectedEvent._def.extendedProps.meetingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#60a5fa',
                                textDecoration: 'none',
                                fontWeight: 600
                              }}
                            >
                              Join Meeting
                            </a>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button 
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ 
                  borderRadius: 2,
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Close
              </Button>
              <Button 
                onClick={handleViewDetails} 
                variant="contained" 
                sx={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4338ca 0%, #4f46e5 100%)'
                  }
                }}
              >
                View Details
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Event List Modal */}
      <Dialog 
        open={eventListOpen} 
        onClose={handleEventListClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}>
              {eventListType === 'assessment' ? <Assessment /> : 
               eventListType === 'session' ? <VideoCall /> : 
               eventListType === 'interview' ? <Person /> : <CalendarToday />}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {eventListType === 'all' ? 'All Events' : 
                 eventListType === 'assessment' ? 'Assessments' : 
                 eventListType === 'interview' ? 'Interviews' : 'Sessions'}
              </Typography>
              <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                {user?.role === 'administrator' ? 'All events' :
                 user?.role === 'assessor' ? 'Your assessments' :
                 'Your appointments'}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={handleEventListClose}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0, bgcolor: 'transparent' }}>
          <Tabs 
            value={eventListTab} 
            onChange={handleTabChange}
            sx={{ 
              borderBottom: 1, 
              borderColor: 'rgba(255, 255, 255, 0.2)',
              bgcolor: 'transparent',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': {
                  color: 'rgba(255, 255, 255, 0.95)'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'rgba(255, 255, 255, 0.95)'
              }
            }}
          >
            <Tab label={`Future (${eventListTab === 0 ? getEventsForList().length : getFutureEvents().filter(e => eventListType === 'all' || e.type === eventListType).length})`} />
            <Tab label={`Past (${eventListTab === 1 ? getEventsForList().length : getPastEvents().filter(e => eventListType === 'all' || e.type === eventListType).length})`} />
          </Tabs>
          
          <Box sx={{ maxHeight: '400px', overflow: 'auto', bgcolor: 'transparent' }}>
            {getEventsForList().length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  No {eventListTab === 0 ? 'future' : 'past'} events found
                </Typography>
              </Box>
            ) : (
              <List sx={{ bgcolor: 'transparent' }}>
                {getEventsForList().map((event, index) => (
                  <React.Fragment key={event.id}>
                    <ListItem 
                      sx={{ 
                        py: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'rgba(79, 70, 229, 0.05)'
                        }
                      }}
                      onClick={() => {
                        setSelectedEvent({ 
                          _def: { 
                            title: event.title, 
                            extendedProps: { 
                              type: event.type, 
                              counsellorName: event.counsellor,
                              client: event.client
                            } 
                          }, 
                          start: event.start, 
                          end: event.end 
                        });
                        handleEventListClose();
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ 
                          bgcolor: event.type === 'assessment' ? '#6366f1' : event.type === 'interview' ? '#f59e0b' : '#48bb78',
                          width: 40,
                          height: 40
                        }}>
                          {event.type === 'assessment' ? <Assessment /> : event.type === 'interview' ? <Person /> : <VideoCall />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.95)' }}>
                            {event.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {event.type === 'session' ? `Session with ${event.counsellor}` : 
                               event.type === 'interview' ? `Interview with ${event.interviewerName || 'Interviewer'}` :
                               'Assessment'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              {new Date(event.start).toLocaleDateString('en-GB', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })} at {new Date(event.start).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip 
                        label={event.type === 'assessment' ? 'Assessment' : event.type === 'interview' ? 'Interview' : 'Session'}
                        size="small"
                        sx={{
                          bgcolor: event.type === 'assessment' ? '#6366f1' : event.type === 'interview' ? '#f59e0b' : '#48bb78',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </ListItem>
                    {index < getEventsForList().length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleEventListClose}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;
